import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import WithRoutify from "../../hoc/WithRoutify";
import UnifiedSessionService from "../../services/UnifiedSessionService";
import UnifiedLoginScreen from "./UnifiedLoginScreen";
import UnifiedCompletedWarning from "./UnifiedCompletedWarning";
import { UnifiedSessionProvider, useUnifiedSession } from "./UnifiedSessionContext";
import { clearUnifiedIntakeJustCompleted } from "../../utils/unifiedIntakeConstants";

const MessageScreen = ({ title, message }) => (
  <div className="sh_login">
    <div className="sh_login-bleed">
      <main className="sh_login-bg">
        <div className="container sh_login-container">
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="d-flex justify-content-center">
                <div className="sh_login-card">
                  <div className="sh_login-card-inner text-center">
                    <div className="sh_login-card-title">{title}</div>
                    <p className="mt-3 text-muted">{message}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
);

const UnifiedPatientStartInner = (props) => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  const employeeIdFromQuery = new URLSearchParams(location.search).get("employee_id");
  const [resolvedEmployeeId, setResolvedEmployeeId] = useState(employeeIdFromQuery);
  const [screenState, setScreenState] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [continueAfterCompletion, setContinueAfterCompletion] = useState(false);
  const [otpOnlyMode, setOtpOnlyMode] = useState(false);
  const [knownPatientFormId, setKnownPatientFormId] = useState(null);
  const { setSessionToken, setSessionData } = useUnifiedSession();
  const loader = props.Loader;

  const handleLoginError = useCallback((msg) => {
    if (msg?.includes("completed")) {
      try {
        sessionStorage.removeItem("unified_continue_anyway_flow");
      } catch {
        /* ignore */
      }
      setContinueAfterCompletion(false);
      setScreenState("completed_warning");
    }
    if (msg?.includes("expired")) {
      setScreenState("error");
      setErrorMessage("This link has expired. Please contact your provider.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!token) {
        setScreenState("error");
        setErrorMessage("Invalid link.");
        return;
      }
      loader(true);
      const data = await UnifiedSessionService.getSessionStatus(token);
      if (cancelled) return;
      loader(false);
      if (!data || data.status === "not_found") {
        setScreenState("error");
        setErrorMessage("Invalid link.");
        return;
      }
      if (data.patient_form_id) {
        setKnownPatientFormId(data.patient_form_id);
      }
      const emp = employeeIdFromQuery || data.employee_id || null;
      setResolvedEmployeeId(emp);
      const packetAlreadyCompleted =
        data.form_completed === true ||
        data.status === "completed" ||
        data.is_completed === true;

      if (packetAlreadyCompleted) {
        if (!continueAfterCompletion) {
          try {
            sessionStorage.removeItem("unified_continue_anyway_flow");
          } catch {
            /* ignore */
          }
        }
        if (continueAfterCompletion) {
          setScreenState("login");
          return;
        }
        try {
          clearUnifiedIntakeJustCompleted();
        } catch {
          /* ignore */
        }
        setScreenState("completed_warning");
        return;
      }
      try {
        sessionStorage.removeItem("unified_continue_anyway_flow");
      } catch {
        /* ignore */
      }
      if (data.is_expired) {
        setScreenState("error");
        setErrorMessage("This link has expired. Please contact your provider.");
        return;
      }
      setScreenState("login");
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [token, employeeIdFromQuery, continueAfterCompletion, loader]);

  if (screenState === "loading") return <MessageScreen title="Loading" message="Please wait..." />;
  if (screenState === "error") return <MessageScreen title="Link Error" message={errorMessage} />;
  if (screenState === "completed_warning") {
    return (
      <UnifiedCompletedWarning
        onBackToLogin={() => {
          setContinueAfterCompletion(false);
          setOtpOnlyMode(true);
          setScreenState("login");
        }}
        onContinueAnyway={() => {
          try {
            sessionStorage.setItem("unified_continue_anyway_flow", "1");
          } catch {
            /* ignore */
          }
          setContinueAfterCompletion(true);
          setOtpOnlyMode(false);
          setScreenState("login");
        }}
      />
    );
  }

  return (
    <UnifiedLoginScreen
      key={`${continueAfterCompletion}-${otpOnlyMode}`}
      token={token}
      employeeId={resolvedEmployeeId}
      continueAfterCompletion={continueAfterCompletion}
      otpOnlyMode={otpOnlyMode}
      knownPatientFormId={knownPatientFormId}
      Loader={props.Loader}
      Toast={props.Toast}
      setSessionToken={setSessionToken}
      setSessionData={setSessionData}
      onLoginError={handleLoginError}
    />
  );
};

const UnifiedPatientStart = (props) => (
  <UnifiedSessionProvider>
    <UnifiedPatientStartInner {...props} />
  </UnifiedSessionProvider>
);

export default WithRoutify(UnifiedPatientStart);
